import { createContext, useContext, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLocalStorage } from './useLocalStorage'
import { UserData, UserRole } from '../constants/Usertype'
import { jwtDecode } from 'jwt-decode'
import { useProactiveTokenRefresh } from './useProactiveTokenRefresh'

const AuthContext = createContext<any>(null)

export const AuthProvider = ({ children }: { children: any }) => {
  const [user, setUser] = useLocalStorage('adminuser', null)
  const [selectedVerticle, setSelectedVerticle] = useLocalStorage(
    'selectedVerticle',
    null,
  )
  const navigate = useNavigate()

  const login = async (data: UserData) => {
    const roles: UserRole[] = []

    try {
      // Decode the JWT token
      const decodedToken: any = jwtDecode(data.token)

      // Extract roles from decoded token
      const tokenRoles = decodedToken.user.roles || []
      if (tokenRoles.includes('administrator')) {
        roles.push({ name: 'Vehicle', key: 'vehicle_admin' })
        roles.push({ name: 'Property', key: 'property_admin' })
        roles.push({ name: 'Rewards', key: 'reward_admin' })
        roles.push({ name: 'Banner management', key: 'banner_admin' })
        roles.push({ name: 'Voucher management', key: 'voucher_admin' })
        roles.push({ name: 'Location Management', key: 'location_admin' })
        roles.push({ name: 'Users', key: 'user_admin' })
        roles.push({ name: 'Jobs', key: 'job_admin' })
      } else {
        if (tokenRoles.includes('vehicle_admin')) {
          roles.push({ name: 'Vehicle', key: 'vehicle_admin' })
        }
        if (tokenRoles.includes('property_admin')) {
          roles.push({ name: 'Property', key: 'property_admin' })
        }
        if (tokenRoles.includes('reward_admin')) {
          roles.push({ name: 'Rewards', key: 'reward_admin' })
        }
        if (tokenRoles.includes('banner_admin')) {
          roles.push({ name: 'Banner management', key: 'banner_admin' })
        }
        if (tokenRoles.includes('voucher_admin')) {
          roles.push({ name: 'Voucher management', key: 'voucher_admin' })
        }
        if (tokenRoles.includes('location_admin')) {
          roles.push({ name: 'Location Management', key: 'location_admin' })
        }
        if (tokenRoles.includes('user_admin')) {
          roles.push({ name: 'Users', key: 'user_admin' })
        }
        if (tokenRoles.includes('admin')) {
          roles.push({ name: 'Jobs', key: 'job_admin' })
        }
        if (tokenRoles.includes('deals_reviewer')) {
          roles.push({ name: 'Rewards', key: 'deals_reviewer' })
        }
        // "User Viewer" — read-only user search. It has no vertical of its own and
        // shares the Users vertical; the listing/detail pages gate what it can do.
        // Normalised the same way as authService.getRolesFromToken() so both
        // 'user_viewer' and 'User Viewer' match. Skipped when the user is already a
        // user_admin, to avoid pushing the Users vertical twice.
        const normalizedRoles: string[] = (tokenRoles as string[]).map((role) =>
          String(role).toLowerCase().replace(/\s+/g, '_'),
        )
        if (
          normalizedRoles.includes('user_viewer') &&
          !normalizedRoles.includes('user_admin')
        ) {
          roles.push({ name: 'Users', key: 'user_admin' })
        }
      }

      // Chat Moderation is restricted to the dedicated 'chat_admin' role ONLY —
      // it is deliberately NOT granted to a plain 'administrator'. Direct token
      // check (no admin auto-grant), so it's hidden for everyone without it.
      const chatAdminRoles = (tokenRoles as string[]).map((r) =>
        String(r).toLowerCase().replace(/\s+/g, '_'),
      )
      if (chatAdminRoles.includes('chat_admin')) {
        roles.push({ name: 'Chat Moderation', key: 'chat_admin' })
      }

      if (roles.length === 0) {
        throw new Error('You do not have permission to access the backoffice.')
      }

      const defaultRole = roles[0]
      setSelectedVerticle(defaultRole.key)
      setUser({ ...data, user: { ...data.user, roles: roles } })

      setTimeout(() => {
        if (defaultRole.key === 'location_admin') navigate('/locationManagement/country')
        // User Management is the landing page for the Users vertical. It is also the only
        // page the read-only 'user_viewer' role can use, so it must not be the dashboard.
        if (defaultRole.key === 'user_admin') navigate('/users')
        else if (defaultRole.key === 'job_admin') navigate('/jobs/analytics')
        else if (defaultRole.key === 'deals_reviewer') navigate('/approval-requests')
        else if (defaultRole.key === 'chat_admin') navigate('/chat/reports')
        else if (defaultRole.key === 'vehicle_admin' || roles.length > 1)
          navigate('/adsmanagement')
        else navigate('/subscriptionmanagement')
      }, 500)
    } catch (error: any) {
      console.error('Error decoding token:', error)
      throw new Error(error.message || 'Invalid token format')
    }
  }

  const logout = useCallback(() => {
    setUser(null)
    navigate('/login', { replace: true })
  }, [setUser, navigate])

  // Keep the session alive: refresh the access token before it expires so a
  // short-lived qat doesn't drop the admin mid-session. Logs out if the
  // refresh token is expired/revoked.
  useProactiveTokenRefresh({ onExpired: logout })

  const value = useMemo(
    () => ({
      user,
      login,
      logout,
    }),
    [user],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  return useContext(AuthContext)
}
